import { Button, IconName, OptionList, Popover } from "@introist/react-foundation/v2";
import { Resource, useResources, useResourceTypes } from "./useResources";
import { useCallback, useMemo, useState } from "react";
import { compact } from "lodash";

export type ResourceSelectorListProps = {
  resourceType?: string;
  onSelect: (resource: Resource) => unknown;
};

export const ResourceSelectorList = ({ resourceType, onSelect }: ResourceSelectorListProps) => {
  const { resourceTypes, loading: typesLoading } = useResourceTypes();

  const [type, setType] = useState<string | undefined>(resourceType);

  const { resources, loading: resourcesLoading } = useResources(type ?? "", { enabled: !!type });

  const [selecting, setSelecting] = useState(false);

  const typeOptions = useMemo(() => {
    return (resourceTypes ?? []).map(t => ({
      key: t.type,
      title: t.title,
      onClick: () => setType(t.type)
    }));
  }, [resourceTypes]);

  const resourceOptions = useMemo(() => {
    return (resources ?? []).map(r => ({
      key: `resource:${r.type}:${r.id}`,
      title: r.title,
      onClick: async () => {
        setSelecting(true);
        await Promise.resolve(onSelect(r)).finally(() => setSelecting(false));
      }
    }));
  }, [resources, onSelect]);

  const back = useMemo(() => {
    if (type) {
      return {
        key: "back",
        title: "Back",
        startAdornmentIcon: "arrowLeft" as IconName,
        onClick: () => setType(undefined)
      };
    }
    return null;
  }, [type]);

  return (
    <OptionList
      actions={compact([back])}
      options={type ? resourceOptions : typeOptions}
      loading={typesLoading || (!!type && resourcesLoading) || selecting}
      searchable
      searchPlaceholder="Search"
      clearSearchOnOptionsChange
    />
  );
};

export const ResourceSelector = ({
  placeholder = "Add resource",
  ...props
}: ResourceSelectorListProps & { placeholder?: string }) => {
  const [ref, setRef] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);

  const onSelect = useCallback(
    async (resource: Resource) => {
      await props.onSelect(resource);
      setOpen(false);
    },
    [props]
  );

  return (
    <div ref={setRef}>
      <Button variant="blended" size="small" startIcon="plusSmall" onClick={() => setOpen(true)}>
        {placeholder}
      </Button>
      <Popover
        referenceElement={ref}
        open={open}
        onClose={() => setOpen(false)}
        attachToRef={false}
      >
        {open && <ResourceSelectorList {...props} onSelect={onSelect} />}
      </Popover>
    </div>
  );
};
